/* MobileGlassHeader — shared fixed glass-morphic header for every mobile
   page (Courts, Achievements, Leaderboard, Laws, Cassation, …).

   Why a portal?
     A fixed-position element inside a CSS containing block (any ancestor
     with `transform`, `filter`, `perspective`, `will-change`) is
     positioned relative to that ancestor, NOT the viewport — making the
     header appear to scroll. Rendering through `createPortal(...,
     document.body)` escapes any such ancestor and pins the header to
     the real viewport.

   Why one component?
     Before this, each page hand-rolled its own fixed header with subtle
     differences in safe-area padding, blur amount, height, and back-
     button styling. This single component is the source of truth so any
     future tweak ripples to every page automatically.

   Usage:
     <MobileGlassHeader
       title="غُرفة إنجازاتي"
       subtitle={`${count} إنجاز محقَّق`}
       backTo="/account"
       rightSlot={<button>...</button>}   // optional, e.g. share / settings
       testid="achievements-header"
     /> */
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";

export default function MobileGlassHeader({
  title,
  subtitle,
  backTo = "/",
  rightSlot = null,
  testid = "mobile-glass-header",
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <header
      className="lg:hidden fixed top-0 inset-x-0 z-30"
      style={{
        paddingTop: "calc(max(env(safe-area-inset-top, 0px), 44px) + 4px)",
        paddingBottom: "10px",
        background: "rgba(255,255,255,0.62)",
        backdropFilter: "blur(28px) saturate(190%)",
        WebkitBackdropFilter: "blur(28px) saturate(190%)",
        borderBottom: "1px solid rgba(10,25,47,0.06)",
        boxShadow: "0 6px 24px -12px rgba(10,25,47,0.10)",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 px-4">
        <Link
          to={backTo}
          data-testid={`${testid}-back`}
          aria-label="رجوع"
          className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center active:scale-95 transition-transform shrink-0"
        >
          <CaretRight size={20} weight="bold" className="text-[#0A192F]" />
        </Link>
        <div className="flex-1 text-center min-w-0">
          <h1 className="text-[17px] font-bold text-[#0A192F] leading-tight truncate">{title}</h1>
          {subtitle && (
            <p className="text-[11px] text-[#5A6470] mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
        {/* Right slot: keeps space symmetric even when empty so the title
            stays visually centered between the two 40 px chips. */}
        <div className="w-10 h-10 shrink-0 flex items-center justify-center">
          {rightSlot}
        </div>
      </div>
    </header>,
    document.body,
  );
}

/* Shared full-bleed background gradient (white → grey) used by every
   mobile page. Renders behind everything via z-index: -10. Pair with
   <StatusBarCover /> below so the iOS notch / Android system bar blends
   seamlessly into the page top. */
export function MobilePageBackground() {
  return (
    <>
      <div
        className="lg:hidden fixed top-0 inset-x-0 pointer-events-none"
        style={{
          height: "calc(env(safe-area-inset-top, 0px) + 8px)",
          background: "#FFFFFF",
          zIndex: -5,
        }}
        aria-hidden="true"
      />
      <div
        className="lg:hidden fixed pointer-events-none -z-10"
        style={{
          top: "-40vh",
          bottom: "-20vh",
          left: 0,
          right: 0,
          background:
            "linear-gradient(180deg,#FFFFFF 0%,#FFFFFF calc(40vh + env(safe-area-inset-top, 0px) + 60px),#F1F1F1 60%,#E0E0E0 85%,#D5D5D5 100%)",
        }}
        aria-hidden="true"
      />
    </>
  );
}

/* Standard top padding to use on the `<main>` element so content clears
   the fixed glass header. Constant so every page uses the SAME value. */
export const MOBILE_HEADER_CONTENT_OFFSET = "130px";
